import Stripe from 'stripe';

import { notify } from 'src/clients/ApiService';
import {
  OnboardingStatus,
  OnboardingTask,
} from 'src/entities/OnboardingStatus';
import { StudioChecklistTaskStatus } from 'src/features/appBuilder/getting-started-checklist/studioChecklist';
import { setTrialState } from 'src/store/user/actions';

import { ApiMethods, ApiTags, appAPI } from '.';

export interface PatchDismissalStateParams {
  fields: Partial<OnboardingStatus['fields']>;
}

export interface GetOnboardingTaskStatusParams {
  subscriptions?: Stripe.Subscription[];
  // When true, the studio three-state `status` is derived onto each task (see below).
  // Left off for classic consumers so the response is returned untouched.
  isStudioUser?: boolean;
}

// Studio FTUX mock: the backend does not yet return the studio three-state `status`
// (see the Studio Onboarding & FTUX TDD). Until it does, derive it from the classic
// `done` flag so consumers can read `task.status` off the response directly. Remove
// this once the backend populates `status`.
const withStudioTaskStatus = (task: OnboardingTask): OnboardingTask => ({
  ...task,
  status:
    task.status ??
    (task.done
      ? StudioChecklistTaskStatus.Todo
      : StudioChecklistTaskStatus.Todo),
});

const withStudioTaskStatuses = (
  onboardingStatus: OnboardingStatus,
): OnboardingStatus => ({
  ...onboardingStatus,
  fields: {
    ...onboardingStatus.fields,
    tasks: onboardingStatus.fields.tasks?.map(withStudioTaskStatus),
  },
  additionalFields: {
    ...onboardingStatus.additionalFields,
    tasks: onboardingStatus.additionalFields.tasks?.map(withStudioTaskStatus),
  },
});

export const improvedOnboardingApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    patchDismissalState: build.mutation<
      OnboardingStatus,
      PatchDismissalStateParams
    >({
      query: (entity) => ({
        path: `/onboarding-task-status`,
        method: ApiMethods.patch,
        options: {
          body: {
            ...entity,
          },
        },
      }),
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          improvedOnboardingApi.util.updateQueryData(
            'getOnboardingTaskStatus',
            {},
            (draft) => {
              draft.fields.dismissed = Boolean(input.fields.dismissed);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          // if there is an error, we need to revert the state
          patch.undo();
          notify({
            status: 'error',
            errorMessage: `Card can not be \`${
              input.fields.dismissed ? 'dismissed' : 'un-dismissed'
            }\``,
            error,
            dispatch,
          });
        }
      },
    }),
    getOnboardingTaskStatus: build.query<
      OnboardingStatus,
      GetOnboardingTaskStatusParams
    >({
      query: () => ({
        path: '/onboarding-task-status',
        method: ApiMethods.get,
        options: {},
      }),
      transformResponse: (
        response: OnboardingStatus,
        _meta,
        arg: GetOnboardingTaskStatusParams,
      ) => (arg?.isStudioUser ? withStudioTaskStatuses(response) : response),
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          dispatch(setTrialState(input.subscriptions, result.data));
        } catch (error) {
          // we want to fail silently because it means that query failed which will happen only if user doesn't have permission
          // and if user doesn't have permission then we don't want to show onboarding tasks to them any way
          console.warn(error);
        }
      },
      providesTags: [ApiTags.onboarding_task_status],
    }),
  }),
});

export const {
  usePatchDismissalStateMutation,
  useLazyGetOnboardingTaskStatusQuery,
  useGetOnboardingTaskStatusQuery,
} = improvedOnboardingApi;
